import React from "react";
import { Brand, Button } from "@ozpanel/ui";

// The credential of the screens that work WITHOUT a session — recovery mode and the install screen.
// On a node whose database is down, or that is still being installed, no login exists; these screens
// authenticate every call with the node's recovery token or a recovery grant for the node instead.
// Kept in this tab's sessionStorage only, and shared, so entering it once serves both screens.

export const API = "/api/v1";
const TOKEN_KEY = "ozpanel.recoveryToken";

export type Json = Record<string, any>;

export function readToken(): string {
  try {
    return sessionStorage.getItem(TOKEN_KEY) ?? "";
  } catch {
    return "";
  }
}

// The credential in use. sessionStorage carries it across reloads of this tab; this variable carries
// it where sessionStorage cannot (a private window).
let held = readToken();

export function storeToken(t: string) {
  held = t;
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* a private window: the token lives only in memory, which is fine */
  }
}

/** api calls a recovery route with the held credential. Named and shaped like the consoles' other
 *  API helpers — path first — so the console reach checks (internal/console) see every call. */
export async function api<T = Json>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(API + path, {
    ...init,
    headers: { Authorization: `Bearer ${held}`, "Content-Type": "application/json" },
  });
  let env: Json = {};
  try {
    env = await res.json();
  } catch {
    /* non-JSON: reported below */
  }
  if (!res.ok || env.ok === false) {
    const err = new Error(env.error || `${res.status} ${res.statusText}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return env.data as T;
}

/** The token held for this tab, with sign-in and sign-out. */
export function useRecoveryCredential() {
  const [token, setToken] = React.useState(readToken);
  const signIn = (t: string) => {
    storeToken(t);
    setToken(t);
  };
  const signOut = () => {
    storeToken("");
    setToken("");
  };
  return { token, signIn, signOut };
}

export function TokenSignIn({
  title,
  subtitle,
  error,
  onSignIn,
}: {
  title: string;
  subtitle: string;
  error?: string | null;
  onSignIn: (token: string) => void;
}) {
  const [draft, setDraft] = React.useState("");
  return (
    <main className="grid min-h-dvh place-items-center p-6">
      <div className="w-full max-w-[430px]">
        <div className="mb-7 text-center">
          <div className="mb-4 flex justify-center">
            <Brand />
          </div>
          <h1 className="text-[19px] font-[680] tracking-[-0.02em]">{title}</h1>
          <p className="mt-1.5 text-[14px] text-muted">{subtitle}</p>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const t = draft.trim();
            if (t) onSignIn(t);
            setDraft("");
          }}
          className="rounded-panel border border-line bg-gradient-to-b from-surface to-bg2 p-[26px]"
        >
          {error && (
            <p className="mb-4 rounded-lg border border-line bg-critsoft px-3 py-2.5 text-[13px] text-crit">{error}</p>
          )}
          <p className="mb-4 text-[13px] leading-relaxed text-muted">
            Enter this node's recovery token — print it on the node with{" "}
            <code className="font-mono text-fg">ozpanel admin recovery-token</code> (the install creates it) — or a
            recovery grant for this node, issued on the primary or a successor (Cluster → Recovery → Recovery grants,
            or <code className="font-mono text-fg">ozpanel admin recovery-grant</code>). Every action taken is
            recorded on this node.
          </p>
          <label htmlFor="rtoken" className="mb-[7px] block text-[12px] font-medium text-muted">
            Recovery token or grant
          </label>
          <input
            id="rtoken"
            type="password"
            autoComplete="off"
            autoFocus
            required
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="mb-4 w-full rounded-[9px] border border-line2 bg-bg px-[13px] py-[11px] font-mono text-[14px] text-fg outline-none focus:border-accent"
          />
          <Button type="submit" variant="accent" className="w-full py-3 text-[15px]">
            Continue
          </Button>
          <p className="mt-3 text-center text-[12.5px]">
            <a className="text-accent hover:underline" href={import.meta.env.BASE_URL}>
              Back to sign in
            </a>
          </p>
        </form>
      </div>
    </main>
  );
}
